import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { map, pick } from 'ramda';
import { Modal, Button } from 'antd';
import Translate from 'react-translate-component';

import { mapIndexed } from 'utils/ramdaAdditions';
import * as actions from 'constants/actionTypes';
import { closeModal, removeModal, hideModal, showModal, closeAllModals } from 'actions/modalActions';

import CatchError from 'components/common/CatchError';
import CustomPushPermissionModal from 'components/modals/confirm/CustomPushPermissionModal';

import {
    ApprovePersonModal,
    RejectPersonModal,
    ApproveProjectModal,
    RejectProjectModal,
    CancelRequestModal,
    ApproveApplicationModal,
    CancelApplicationModal,
    MetroApproveApplicationModal,
    MetroRejectApplicationModal,
    MetroCancelApplicationModal,
} from 'components/modals/approvals/ApprovalModal';
import PersonStateModal from 'components/modals/personState/PersonStateModal';
import WorksheetUnsupportedModal from 'components/modals/worksheetUnsupported/WorksheetUnsupportedModal';
import WorksheetSettingsModal from 'components/modals/tableSettings/WorksheetSettingsModal';
import WorksheetMassSettingsModal from 'components/modals/tableSettings/WorksheetMassSettingsModal';
import ProjectSettingsModal from 'components/modals/tableSettings/ProjectSettingsModal';
import ProjectMassSettingsModal from 'components/modals/tableSettings/ProjectMassSettingsModal';
import CreateEventModal from 'components/modals/events/CreateEventModal';
import EventScriptModal from 'components/modals/events/EventScriptModal';
import CreateSotrMeetModal from 'components/modals/calendar/meet/CreateSotrMeetModal';
import CreateTaskModal from 'components/modals/calendar/task/CreateTaskModal';
import EditTaskModal from 'components/modals/calendar/task/EditTaskModal';
import EditSotrMeetModal from 'components/modals/calendar/meet/EditSotrMeetModal';
import ConfirmModal from 'components/modals/confirm/ConfirmModal';
import MassStageModal from 'components/modals/massStage/MassStageModal';
import DocumentDownloadModal from './modals/document/DocumentDownloadModal';
import ApplicationCreateModal from 'components/modals/education/application/ApplicationCreateModal';
import EducationEventModal from 'components/modals/education/calendar/EducationEventModal';
import ActionVacancyModal from 'components/modals/education/actionVacancy/ActionVacancyModal';
import ProfileAvatarModal from 'components/modals/profile/ProfileAvatarModal';
import ScenarioTestModal from 'components/modals/education/scenario/ScenarioTestModal';
import ScenarioModal from 'components/modals/education/scenario/ScenarioModal';
import TrainerMarkModal from 'components/modals/education/trainer/TrainerMarkModal';
import ScormModal from 'components/modals/education/scenario/ScormModal';
import ScenarioBookModal from 'components/modals/education/scenario/ScenarioBookModal';
import MetroRequestRouteModal from 'components/modals/metro/MetroRequestRouteModal';
import MetroRequestPositionModal from 'components/modals/metro/MetroRequestPositionModal';

const ModalError = ({ reset, closeAll }) => (
    <Modal width={300} visible={true} footer={null} closable={false} className="modal-error">
        <div className="catched-error">
            <Translate content="errors.couldNotOpenWindow" component="h3" className="m-b-15" />
            <Button type="blue" onClick={reset}>
                <Translate content="common.refresh" />
            </Button>
            <Button onClick={closeAll}>
                <Translate content="modal_settings.cancel" />
            </Button>
        </div>
    </Modal>
);
const modalActionsPropTypes = {
    close: PropTypes.func.isRequired,
    remove: PropTypes.func.isRequired,
    hide: PropTypes.func.isRequired,
    show: PropTypes.func.isRequired,
    closeAll: PropTypes.func.isRequired,
};

export const modalPropTypes = {
    ...modalActionsPropTypes,
    params: PropTypes.any,
    index: PropTypes.number,
    modal: PropTypes.shape({
        visible: PropTypes.bool.isRequired,
        width: PropTypes.number.isRequired,
        onClose: PropTypes.func.isRequired,
    }),
};

const stateToProps = ({ modals, notification }) => ({
    modals,
    notificationPermissionRequest: notification.notificationPermissionRequest
});

const dispatchToProps = {
    close: closeModal,
    remove: removeModal,
    hide: hideModal,
    show: showModal,
    closeAll: closeAllModals
};

const modalComponents = {
    [actions.OPEN_MODAL_DOCUMENT_LINK]: DocumentDownloadModal,
    [actions.OPEN_MODAL_WORKSHEET_UNSUPPORTED]: WorksheetUnsupportedModal,
    [actions.OPEN_MODAL_PROJECT_SETTINGS]: ProjectSettingsModal,
    [actions.OPEN_MODAL_WORKSHEET_SETTINGS]: WorksheetSettingsModal,
    [actions.OPEN_MODAL_APPROVE_PERSON]: ApprovePersonModal,
    [actions.OPEN_MODAL_REJECT_PERSON]: RejectPersonModal,
    [actions.OPEN_MODAL_APPROVE_PROJECT]: ApproveProjectModal,
    [actions.OPEN_MODAL_REJECT_PROJECT]: RejectProjectModal,
    [actions.OPEN_MODAL_CANCEL_REQUEST]: CancelRequestModal,
    [actions.OPEN_MODAL_EVENT_CREATE]: CreateEventModal,
    [actions.OPEN_MODAL_EVENT_SCRIPT]: EventScriptModal,
    [actions.OPEN_MODAL_SOTRMEET_CREATE]: CreateSotrMeetModal,
    [actions.OPEN_MODAL_TASK_CREATE]: CreateTaskModal,
    [actions.OPEN_MODAL_TASK_UPDATE]: EditTaskModal,
    [actions.OPEN_MODAL_SOTRMEET_UPDATE]: EditSotrMeetModal,
    [actions.OPEN_MODAL_CONFIRM]: ConfirmModal,
    [actions.OPEN_MODAL_PROJECT_MASS_SETTINGS]: ProjectMassSettingsModal,
    [actions.OPEN_MODAL_WORKSHEET_MASS_SETTINGS]: WorksheetMassSettingsModal,
    [actions.OPEN_MODAL_STATE_PERSON]: PersonStateModal,
    [actions.OPEN_MODAL_MASS_STAGE]: MassStageModal,
    [actions.OPEN_MODAL_CREATE_APPLICATION]: ApplicationCreateModal,
    [actions.OPEN_MODAL_EDUCATION_EVENT]: EducationEventModal,
    [actions.OPEN_MODAL_AVATAR]: ProfileAvatarModal,
    [actions.OPEN_MODAL_APPLICATION_APPROVE]: ApproveApplicationModal,
    [actions.OPEN_MODAL_APPLICATION_CANCEL]: CancelApplicationModal,
    [actions.OPEN_MODAL_ACTION_VACANCY]: ActionVacancyModal,
    [actions.OPEN_MODAL_SCENARIO_TEST]: ScenarioTestModal,
    [actions.OPEN_MODAL_SCENARIO]: ScenarioModal,
    [actions.OPEN_MODAL_TRAINER_MARK]: TrainerMarkModal,
    [actions.OPEN_MODAL_SCORM]: ScormModal,
    [actions.OPEN_MODAL_SCENARIO_BOOK]: ScenarioBookModal,
    [actions.OPEN_MODAL_METRO_REQUEST_ROUTE]: MetroRequestRouteModal,
    [actions.OPEN_MODAL_METRO_REQUEST_POSITION]: MetroRequestPositionModal,
    [actions.OPEN_MODAL_METRO_REQUEST_APPROVE]: MetroApproveApplicationModal,
    [actions.OPEN_MODAL_METRO_REQUEST_REJECT]: MetroRejectApplicationModal,
    [actions.OPEN_MODAL_METRO_REQUEST_CANCEL]: MetroCancelApplicationModal,
};

const getModalActions = pick(['close', 'remove', 'hide', 'show', 'closeAll']);

@connect(stateToProps, dispatchToProps)
export default class Modals extends Component {
    static propTypes = {
        ...modalActionsPropTypes,
        modals: PropTypes.array.isRequired
    };

    renderOnError = (reset) => {
        const { closeAll } = this.props;

        return <ModalError reset={reset} closeAll={closeAll} />;
    }

    getModalComponent = ({ type, params, visible, close }, index, length) => {
        const actions = map(action => action.bind(null, index), getModalActions(this.props));
        const ModalComponent = modalComponents[type];

        const modalProps = {
            visible: index + 1 === length && visible && !close,
            width: ModalComponent.width || 768,
            onCancel: actions.close,
            afterClose: () => close && actions.remove(),
        };

        return (
            <CatchError renderOnError={this.renderOnError} key={index}>
                <ModalComponent {...actions} index={index} modal={modalProps} params={params} />
            </CatchError>
        );
    }

    render() {
        const { modals, notificationPermissionRequest } = this.props;

        return (
            <div>
                { mapIndexed((modal, index) => (
                    this.getModalComponent(modal, index, modals.length)
                ), modals)}
                { notificationPermissionRequest && <CustomPushPermissionModal />}
            </div>
        );
    }
}
